import type { RouteRecordRaw } from "vue-router"
import type { AppSection } from "src/app/sections"

// `public: true` marks routes reachable without authentication (the login flow). The beforeEach
// guard (router/index.ts) reads it off the merged `to.meta`.
//
// `section` says which top-level navigation section a route belongs to — both navigations read it from
// here (see `app/sections.ts`). The paths are FLAT: `/sensors`, not `/monitoring/sensors`. A section is
// a question of navigation and not a property of the thing; in the path, moving an item into another
// section would change its URL.
//
// `view` names the **filter surface**: several route records rendering the same page (list and detail
// for devices and sensors) carry the same value, and navigation inside one surface keeps the filter
// query (`utils/routeQuery.ts`) — without it a click from a detail back to the list discards the very
// filters the row was found with. Explicit on the record rather than derived from the component:
// `component` is a lazy loader here, so two records on one file carry two different functions.
declare module "vue-router" {
  interface RouteMeta {
    public?: boolean
    section?: AppSection
    view?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/login",
    component: () => import("layouts/LoginLayout.vue"),
    meta: { public: true },
    children: [{ path: "", name: "Login", component: () => import("pages/LoginPage.vue") }],
  },
  {
    path: "/",
    component: () => import("layouts/MainLayout.vue"),
    children: [
      // --- bx:ops (home) ---
      {
        path: "",
        name: "Start",
        component: () => import("pages/IndexPage.vue"),
        meta: { section: "home" },
      },
      {
        // In-app changelog (GitLab releases) — surfaced in the home section's sub-nav.
        path: "changelog",
        name: "Changelog",
        component: () => import("pages/changelog/ChangelogPage.vue"),
        meta: { section: "home" },
      },
      {
        // The scheduler is called „Jobs“ in the navigation, so its path is `/jobs` — one name for the
        // tab, the page title and the path.
        path: "jobs",
        name: "Scheduler",
        component: () => import("pages/scheduler/SchedulerPage.vue"),
        meta: { section: "home" },
      },
      {
        // Admin console — in the home section's sub-nav. NOT gated as a whole: its Labels tab is open to
        // every logged-in user, because every label chip links here to answer where else a label is used
        // and a route guard would make those links dead ends. Each tab gates itself, and the backend
        // enforces the AdminPermission on every admin call.
        path: "administration",
        name: "Administration",
        component: () => import("pages/admin/AdministrationPage.vue"),
        meta: { section: "home" },
      },

      // --- Incidents ---
      {
        path: "incidents",
        name: "IncidentList",
        component: () => import("pages/incident/IncidentListPage.vue"),
        meta: { section: "incident", view: "incidents" },
      },
      {
        path: "incidents/:incidentId",
        name: "IncidentDetail",
        component: () => import("pages/incident/IncidentDetailPage.vue"),
        meta: { section: "incident" },
      },
      // Redirects from old paths exist ONLY where a URL demonstrably lives on outside the app;
      // everything else sits in bookmarks and is deliberately left to 404.
      //
      // `/ui/incident/<id>` stands in every escalation sent BEFORE the route rebuild, and those messages
      // stay in Slack histories and mailboxes for good. The backend writes the plural everywhere today
      // (`NotificationChannel.incidentUrl`, `slack/incident.kt`, `deviceViewLink`), so this redirect
      // serves the archive rather than the current sender.
      //
      // The query is carried along everywhere: the list's filters hang off it at the incidents, the
      // `?site=<name>` deep link at the static sites. A redirect that cuts it leads to the right page
      // but not to the thing that was meant.
      { path: "incident", redirect: (to) => ({ name: "IncidentList", query: to.query }) },
      {
        path: "incident/:incidentId",
        redirect: (to) => ({ name: "IncidentDetail", params: to.params, query: to.query }),
      },
      // Static Sites: `?site=<name>` is the link handed on when it is about ONE site (the list scrolls
      // to it and highlights it), and that link ends up in tickets and chats.
      {
        path: "hosting/static-sites",
        redirect: (to) => ({ name: "StaticSites", query: to.query }),
      },
      {
        // Escalation policies decide who an incident wakes at night: operations, not user
        // administration — hence the incident section and not an administration tab.
        path: "escalation",
        name: "EscalationPolicies",
        component: () => import("pages/incident/EscalationPoliciesPage.vue"),
        meta: { section: "incident" },
      },

      // --- Devices ---
      // Attacks: the fleet view and the same cut for one host. BOTH carry `view`, because both run the
      // same filters — that way a click from the host view into the fleet view takes the filter along
      // instead of losing it. Section `devices` like the tab: the page answers a question about a host,
      // and the ban list hangs off it as a button rather than as a tab of its own.
      {
        path: "attacks",
        name: "Attacks",
        component: () => import("pages/AttacksPage.vue"),
        meta: { section: "devices", view: "attacks" },
      },
      // The ban list stands BEFORE the host route, so that `bans` is not read as a deviceId.
      {
        path: "attacks/bans",
        name: "AttackBans",
        component: () => import("pages/AttackBansPage.vue"),
        meta: { section: "devices", view: "attack-bans" },
      },
      {
        path: "attacks/:deviceId",
        name: "AttacksDevice",
        component: () => import("pages/AttacksPage.vue"),
        meta: { section: "devices", view: "attacks" },
      },
      {
        path: "devices",
        name: "DeviceList",
        component: () => import("pages/device/DeviceListPage.vue"),
        meta: { section: "devices", view: "devices" },
      },
      // A device detail is not its own view — it renders the same list page (tree + detail
      // panel) with the :deviceId driving the selection, so the tree stays visible and the device
      // is pre-selected. Same component as DeviceList → Vue reuses the instance across the two
      // routes (no tree reload on select).
      //
      // CAREFUL with flat paths: a future static child (`/devices/import`) has to be registered BEFORE
      // this param route or the param swallows it — the same ordering rule as for the deployments.
      {
        path: "devices/:deviceId",
        name: "DeviceDetail",
        component: () => import("pages/device/DeviceListPage.vue"),
        meta: { section: "devices", view: "devices" },
      },
      // The fleet's OS facts — under Devices and not under Monitoring: these are a property of the
      // machines, not a judgement about them. One record, because distribution and per-device table are
      // two readings of the same answer rather than two pages.
      {
        path: "facts",
        name: "DeviceFacts",
        component: () => import("pages/device/DeviceFactsPage.vue"),
        meta: { section: "devices" },
      },
      {
        path: "teleport",
        name: "TeleportVersions",
        component: () => import("pages/infrastructure/TeleportVersionsPage.vue"),
        meta: { section: "devices", view: "teleport" },
      },
      // one record with an optional param so both /file-sync and /file-sync/:runId resolve to it —
      // makes a run shareable/deep-linkable and keeps the File Sync sub-nav tab active
      {
        path: "file-sync/:runId?",
        name: "FileSync",
        component: () => import("pages/device/FileSyncPage.vue"),
        meta: { section: "devices" },
      },
      // The agent audit log. In the Devices section because the subject of a row is a HOST — the device
      // detail gets no history of its own, only a jump into this list with that device in scope.
      {
        path: "audit",
        name: "Audit",
        component: () => import("pages/device/AuditPage.vue"),
        meta: { section: "devices" },
      },

      // --- Monitoring ---
      // The global sensor list. The detail is its own route record on the SAME page, so the list stays
      // beside it and a sensor is still shareable.
      {
        path: "sensors",
        name: "SensorList",
        component: () => import("pages/monitoring/SensorListPage.vue"),
        meta: { section: "monitoring", view: "sensors" },
      },
      // Before `sensors/:sensorId`, or the fleet-wide window list would be read as a sensor with the id
      // „maintenance“ — the router takes the first match, and a static segment must win over a parameter.
      {
        path: "sensors/maintenance",
        name: "MaintenanceWindows",
        component: () => import("pages/monitoring/MaintenanceWindowsPage.vue"),
        meta: { section: "monitoring", view: "sensors" },
      },
      {
        path: "sensors/:sensorId",
        name: "SensorDetail",
        component: () => import("pages/monitoring/SensorListPage.vue"),
        meta: { section: "monitoring", view: "sensors" },
      },
      // Integrity, same shape as the sensors: list and detail are two records on the SAME page, so the
      // tree stays beside them and a finding is shareable. There is no intermediate segment
      // (`integrity/findings/:id`) — the id hangs off its collection. The only nesting here is the HOST,
      // and the reason for that stands on its route below.
      {
        path: "integrity",
        name: "IntegrityList",
        component: () => import("pages/monitoring/IntegrityListPage.vue"),
        meta: { section: "monitoring", view: "integrity" },
      },
      {
        path: "integrity/exceptions",
        name: "IntegrityExceptions",
        component: () => import("pages/monitoring/IntegrityExceptionsPage.vue"),
        meta: { section: "monitoring", view: "integrity" },
      },
      // The HOST is in the path with the finding below it — the only nested parameter in this app, and
      // the reason goes past readability: the list is three-column, and a finding is a statement only
      // WITH its host. As `integrity/:findingId?host=…` that belonging is a convention a watcher has to
      // enforce, and a watcher loses against filter→URL navigation (`replace({ query })` keeps the
      // path), leaving one host's finding beside another host's header. In the path it is structural:
      // changing the host changes the segment above it, and a finding cannot outlive its host.
      //
      // Two records, because a host **without** a selected finding is the normal case (the middle
      // column). `integrity/exceptions` stays registered ahead of them, or `:deviceId` swallows the
      // static child — the same ordering trap as `deployments/new` before `:instanceId`.
      {
        path: "integrity/:deviceId",
        name: "IntegrityHost",
        component: () => import("pages/monitoring/IntegrityListPage.vue"),
        meta: { section: "monitoring", view: "integrity" },
      },
      {
        path: "integrity/:deviceId/:findingId",
        name: "IntegrityFinding",
        component: () => import("pages/monitoring/IntegrityListPage.vue"),
        meta: { section: "monitoring", view: "integrity" },
      },
      // The OS package update checker, same shape as sensors and integrity: list and detail are two
      // records on the SAME page. The parameter is a **deviceId** — there is exactly one update record
      // per device, so the detail is the host and not a record of its own.
      {
        path: "updates",
        name: "UpdatesList",
        component: () => import("pages/monitoring/UpdatesListPage.vue"),
        meta: { section: "monitoring", view: "updates" },
      },
      {
        path: "updates/:deviceId",
        name: "UpdateDevice",
        component: () => import("pages/monitoring/UpdatesListPage.vue"),
        meta: { section: "monitoring", view: "updates" },
      },
      // The package overview, behind the updates: the inventory it reads is written by the update run and
      // has no life of its own. List and detail are two records on the SAME page, like the sisters above —
      // the parameter is the PACKAGE NAME, because one package lies across the fleet while one update
      // state belongs to one host. A device scope is a query (`?device=`), not a segment: it narrows the
      // same list rather than opening a different one.
      {
        path: "packages",
        name: "Packages",
        component: () => import("pages/monitoring/PackagesListPage.vue"),
        meta: { section: "monitoring", view: "packages" },
      },
      {
        path: "packages/:name",
        name: "PackageDetail",
        component: () => import("pages/monitoring/PackagesListPage.vue"),
        meta: { section: "monitoring", view: "packages" },
      },
      // CVEs, same shape as the sensors and the integrity findings: list and detail are two records on
      // the SAME page, so the list stays beside the detail and a finding is shareable. The parameter is
      // the CVE id — the list is CVE-centric, because one vulnerability spans the fleet while one update
      // state belongs to one host.
      {
        path: "cves",
        name: "CveList",
        component: () => import("pages/monitoring/CveListPage.vue"),
        meta: { section: "monitoring", view: "cves" },
      },
      // Before the `:cveId` record, or that parameter swallows the static child — the same ordering trap
      // as `integrity/exceptions` above it.
      {
        path: "cves/exceptions",
        name: "CveExceptions",
        component: () => import("pages/monitoring/CveExceptionsPage.vue"),
        meta: { section: "monitoring", view: "cves" },
      },
      {
        path: "cves/:cveId",
        name: "CveDetail",
        component: () => import("pages/monitoring/CveListPage.vue"),
        meta: { section: "monitoring", view: "cves" },
      },
      // The server baseline across the fleet. No detail record of its own: a cell leads to the item on the
      // DEVICE, because that is where it is fixed and excepted.
      {
        path: "baseline",
        name: "BaselineList",
        component: () => import("pages/monitoring/BaselineListPage.vue"),
        meta: { section: "monitoring", view: "baseline" },
      },
      {
        path: "baseline/exceptions",
        name: "BaselineExceptions",
        component: () => import("pages/monitoring/BaselineExceptionsPage.vue"),
        meta: { section: "monitoring", view: "baseline" },
      },
      // Report maintenance as its own item rather than a card in the sensor list: a report is a saved
      // selection over the list, not a property of a single sensor.
      {
        path: "reports",
        name: "SensorReports",
        component: () => import("pages/monitoring/SensorReportsPage.vue"),
        meta: { section: "monitoring" },
      },
      // On-call — duty roster plus pool and hours maintenance. Under Monitoring and not an
      // administration tab: an operations view with a self-service half (everyone maintains their own
      // absences), not an admin table. Not gated as a whole — the page is readable under
      // `OnCallPermissions.Read`, and the write surfaces gate themselves.
      {
        path: "oncall",
        name: "OnCall",
        component: () => import("pages/monitoring/OnCallPage.vue"),
        meta: { section: "monitoring" },
      },
      // Pool, absences and duty hours as their own route rather than a second column: the on-call start
      // page answers who is on duty when, everything else is maintenance one rarely touches. Both views
      // share the local tab row (OnCallTabs).
      {
        path: "oncall/pool",
        name: "OnCallPool",
        component: () => import("pages/monitoring/OnCallPoolPage.vue"),
        meta: { section: "monitoring" },
      },

      // --- Hosting ---
      {
        path: "static-sites",
        name: "StaticSites",
        component: () => import("pages/hosting/StaticSitesPage.vue"),
        meta: { section: "hosting", view: "static-sites" },
      },
      // Deployments: list -> create / detail -> edit / run. All deep-linkable; the Deployments
      // sub-nav tab stays active across the whole subtree (its `to` is the list path prefix).
      // Only the LIST carries `meta.view`: the view is the filter surface whose query may travel
      // (`keepFilterQuery`), and the detail's `?tab=` is not a filter — shared, a click on the tab would
      // carry it into the list URL.
      // Static "new" / "matchers" are registered before ":instanceId" so they can't match the param.
      {
        path: "deployments",
        name: "Deployments",
        component: () => import("pages/hosting/DeploymentsListPage.vue"),
        meta: { section: "hosting", view: "deployments" },
      },
      // The image overview — what the deployments run, seen from the image. The parameter is
      // `registry/repository` and carries slashes, hence the open pattern.
      {
        path: "images",
        name: "Images",
        component: () => import("pages/hosting/ImagesListPage.vue"),
        meta: { section: "hosting", view: "images" },
      },
      {
        path: "images/:reference(.*)",
        name: "ImageDetail",
        component: () => import("pages/hosting/ImagesListPage.vue"),
        meta: { section: "hosting", view: "images" },
      },
      {
        path: "deployments/new",
        name: "DeploymentNew",
        component: () => import("pages/hosting/DeploymentEditorPage.vue"),
        meta: { section: "hosting" },
      },
      {
        path: "deployments/matchers",
        name: "GlobalMatchers",
        component: () => import("pages/hosting/GlobalMatchersPage.vue"),
        meta: { section: "hosting" },
      },
      {
        path: "deployments/:instanceId",
        name: "DeploymentDetail",
        component: () => import("pages/hosting/DeploymentDetailPage.vue"),
        meta: { section: "hosting" },
      },
      {
        path: "deployments/:instanceId/edit",
        name: "DeploymentEdit",
        component: () => import("pages/hosting/DeploymentEditorPage.vue"),
        meta: { section: "hosting" },
      },
      {
        // The live traffic tool of ONE host. Its own route because file selection, window and filters
        // belong in the URL: a shareable link to „this file, this window“ is half the point. No
        // `meta.view` — its query is not a list's filter surface, so a click on the nav tab while
        // standing on a host leads back OUT to the host choice, which is what the tab means.
        // The host is OPTIONAL: without it the page is the tool's entry and asks which one. A second
        // route would mean a second component or a redirect, and the picker belongs where one changes
        // the host anyway.
        path: "traffic/:deviceId?",
        name: "RecentTraffic",
        component: () => import("pages/hosting/RecentTrafficPage.vue"),
        meta: { section: "hosting" },
      },
      {
        path: "deployments/:instanceId/runs/:runId",
        name: "DeploymentRun",
        component: () => import("pages/hosting/DeploymentRunPage.vue"),
        meta: { section: "hosting" },
      },

      // --- Tools ---
      {
        path: "dns-check",
        name: "DnsCheck",
        component: () => import("pages/tools/DnsCheckPage.vue"),
        meta: { section: "tools" },
      },
      // The rules behind the check's „Betreiber“ column. Its own page rather than a dialog: the order is
      // the rule, and reordering wants room.
      {
        path: "dns-check/operators",
        name: "DnsOperators",
        component: () => import("pages/tools/DnsOperatorsPage.vue"),
        meta: { section: "tools", view: "dns-check" },
      },
      // one record with an optional param so both /tls-check and /tls-check/:reportId resolve to it —
      // keeps the TLS-Check sub-nav tab active on a report
      {
        path: "tls-check/:reportId?",
        name: "TlsCheck",
        component: () => import("pages/tools/TlsCheckPage.vue"),
        meta: { section: "tools" },
      },
      // optional param so both /cert-tool and /cert-tool/:itemId use it — keeps the Cert Tool sub-nav
      // tab active on an item detail
      {
        path: "cert-tool/:itemId?",
        name: "CertTool",
        component: () => import("pages/tools/CertToolPage.vue"),
        meta: { section: "tools" },
      },
      {
        path: "cert-viewer",
        name: "CertViewer",
        component: () => import("pages/tools/CertViewerPage.vue"),
        meta: { section: "tools" },
      },
      {
        path: "network-check",
        name: "NetworkCheck",
        component: () => import("pages/tools/NetworkCheckPage.vue"),
        meta: { section: "tools" },
      },

      // --- own profile ---
      {
        path: "me",
        name: "Me",
        component: () => import("pages/me/MePage.vue"),
        meta: { section: "me" },
      },
      {
        path: "me/mcp",
        name: "Mcp",
        component: () => import("pages/me/McpPage.vue"),
        meta: { section: "me" },
      },
    ],
  },
  {
    path: "/:catchAll(.*)*",
    component: () => import("pages/ErrorNotFound.vue"),
  },
]

export default routes
